import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  // Budget actions
  fetchBudgets,
  fetchBudgetById,
  createBudget,
  updateBudget,
  deleteBudget,
  duplicateBudget,

  // Folder actions
  fetchFolders,
  createFolder,
  updateFolder,
  deleteFolder,

  // Swimlane actions
  fetchSwimlanes,
  createSwimlane,
  updateSwimlane,
  deleteSwimlane,

  // Fringe actions
  fetchFringes,
  createFringe,
  updateFringe,
  deleteFringe,

  // User profile actions
  fetchUserProfile,
  updateUserProfile,

  // Swimlane details actions
  fetchSwimlaneDetails,
  createSwimlaneDetails,
  updateSwimlaneDetails,
  deleteSwimlaneDetails,

  // Linked fringes actions
  linkFringes,
  unlinkFringes,

  // Selectors
  selectBudgets,
  selectFolders,
  selectSwimlanes,
  selectSwimlaneDetails,
  selectFringes,
  selectUserProfile,
  selectLoading,
  selectErrors,
  selectSwimlanesByType,
  selectSwimlanesByTypeAndId,
  selectSwimlaneDetailsByType,
  selectSwimlaneDetailsByTypeAndId,
} from 'store/slices/apiSlice';

/**
 * Custom hook that provides API actions and state selectors
 * This centralizes all API interactions and provides a clean interface for components
 */
export const useApiActions = () => {
  const dispatch = useDispatch();

  // State selectors
  const budgets = useSelector(selectBudgets);
  const folders = useSelector(selectFolders);
  const swimlanes = useSelector(selectSwimlanes);
  const swimlaneDetails = useSelector(selectSwimlaneDetails);
  const fringes = useSelector(selectFringes);
  const userProfile = useSelector(selectUserProfile);
  const loading = useSelector(selectLoading);
  const errors = useSelector(selectErrors);

  // Budget actions
  const budgetActions = {
    fetchBudgets: useCallback(() => dispatch(fetchBudgets()), [dispatch]),
    fetchBudgetById: useCallback(
      budgetId => dispatch(fetchBudgetById(budgetId)),
      [dispatch]
    ),
    createBudget: useCallback(
      payload => dispatch(createBudget(payload)),
      [dispatch]
    ),
    updateBudget: useCallback(
      payload => dispatch(updateBudget(payload)),
      [dispatch]
    ),
    deleteBudget: useCallback(
      budgetId => dispatch(deleteBudget(budgetId)),
      [dispatch]
    ),
    duplicateBudget: useCallback(
      payload => dispatch(duplicateBudget(payload)),
      [dispatch]
    ),
  };

  // Folder actions
  const folderActions = {
    fetchFolders: useCallback(() => dispatch(fetchFolders()), [dispatch]),
    createFolder: useCallback(
      payload => dispatch(createFolder(payload)),
      [dispatch]
    ),
    updateFolder: useCallback(
      payload => dispatch(updateFolder(payload)),
      [dispatch]
    ),
    deleteFolder: useCallback(
      folderId => dispatch(deleteFolder(folderId)),
      [dispatch]
    ),
  };

  // Swimlane actions
  const swimlaneActions = {
    fetchSwimlanes: useCallback(
      payload => dispatch(fetchSwimlanes(payload)),
      [dispatch]
    ),
    createSwimlane: useCallback(
      payload => dispatch(createSwimlane(payload)),
      [dispatch]
    ),
    updateSwimlane: useCallback(
      payload => dispatch(updateSwimlane(payload)),
      [dispatch]
    ),
    deleteSwimlane: useCallback(
      payload => dispatch(deleteSwimlane(payload)),
      [dispatch]
    ),
  };

  // Fringe actions
  const fringeActions = {
    fetchFringes: useCallback(
      filters => dispatch(fetchFringes(filters)),
      [dispatch]
    ),
    createFringe: useCallback(
      payload => dispatch(createFringe(payload)),
      [dispatch]
    ),
    updateFringe: useCallback(
      payload => dispatch(updateFringe(payload)),
      [dispatch]
    ),
    deleteFringe: useCallback(
      payload => dispatch(deleteFringe(payload)),
      [dispatch]
    ),
  };

  // User profile actions
  const userActions = {
    fetchUserProfile: useCallback(
      () => dispatch(fetchUserProfile()),
      [dispatch]
    ),
    updateUserProfile: useCallback(
      payload => dispatch(updateUserProfile(payload)),
      [dispatch]
    ),
  };

  // Swimlane details actions
  const swimlaneDetailsActions = {
    fetchSwimlaneDetails: useCallback(
      payload => dispatch(fetchSwimlaneDetails(payload)),
      [dispatch]
    ),
    createSwimlaneDetails: useCallback(
      payload => dispatch(createSwimlaneDetails(payload)),
      [dispatch]
    ),
    updateSwimlaneDetails: useCallback(
      payload => dispatch(updateSwimlaneDetails(payload)),
      [dispatch]
    ),
    deleteSwimlaneDetails: useCallback(
      payload => dispatch(deleteSwimlaneDetails(payload)),
      [dispatch]
    ),
  };

  // Linked fringes actions
  const linkedFringesActions = {
    linkFringes: useCallback(
      payload => dispatch(linkFringes(payload)),
      [dispatch]
    ),
    unlinkFringes: useCallback(
      payload => dispatch(unlinkFringes(payload)),
      [dispatch]
    ),
  };

  // Helper functions for accessing swimlane data
  const getSwimlanesByType = () => selectSwimlanesByType;
  const getSwimlanesByTypeAndId = () => selectSwimlanesByTypeAndId;
  const getSwimlaneDetailsByType = () => selectSwimlaneDetailsByType;
  const getSwimlaneDetailsByTypeAndId = () => selectSwimlaneDetailsByTypeAndId;

  return {
    // State
    budgets,
    folders,
    swimlanes,
    swimlaneDetails,
    fringes,
    userProfile,
    loading,
    errors,

    // Actions
    budgetActions,
    folderActions,
    swimlaneActions,
    swimlaneDetailsActions,
    fringeActions,
    userActions,
    linkedFringesActions,

    // Helper functions
    getSwimlanesByType,
    getSwimlanesByTypeAndId,
    getSwimlaneDetailsByType,
    getSwimlaneDetailsByTypeAndId,
  };
};

/**
 * Hook specifically for budget-related operations
 */
export const useBudgetActions = () => {
  const { budgets, loading, errors, budgetActions } = useApiActions();

  return {
    budgets,
    loading: loading.budgets,
    error: errors.budgets,
    ...budgetActions,
  };
};

/**
 * Hook specifically for folder-related operations
 */
export const useFolderActions = () => {
  const { folders, loading, errors, folderActions } = useApiActions();

  return {
    folders,
    loading: loading.folders,
    error: errors.folders,
    ...folderActions,
  };
};

/**
 * Hook specifically for swimlane-related operations
 */
export const useSwimlaneActions = () => {
  const {
    swimlanes,
    loading,
    errors,
    swimlaneActions,
    getSwimlanesByType,
    getSwimlanesByTypeAndId,
  } = useApiActions();

  return {
    swimlanes,
    loading: loading.swimlanes,
    error: errors.swimlanes,
    getSwimlanesByType,
    getSwimlanesByTypeAndId,
    ...swimlaneActions,
  };
};

/**
 * Hook specifically for fringe-related operations
 */
export const useFringeActions = () => {
  const { fringes, loading, errors, fringeActions } = useApiActions();

  return {
    fringes,
    loading: loading.fringes,
    error: errors.fringes,
    ...fringeActions,
  };
};

/**
 * Hook specifically for user profile operations
 */
export const useUserActions = () => {
  const { userProfile, loading, errors, userActions } = useApiActions();

  return {
    userProfile,
    loading: loading.userProfile,
    error: errors.userProfile,
    ...userActions,
  };
};

/**
 * Hook specifically for swimlane details operations
 */
export const useSwimlaneDetailsActions = () => {
  const {
    swimlaneDetails,
    loading,
    errors,
    swimlaneDetailsActions,
    getSwimlaneDetailsByType,
    getSwimlaneDetailsByTypeAndId,
  } = useApiActions();

  return {
    swimlaneDetails,
    loading: loading.swimlaneDetails,
    error: errors.swimlaneDetails,
    getSwimlaneDetailsByType,
    getSwimlaneDetailsByTypeAndId,
    ...swimlaneDetailsActions,
  };
};

/**
 * Hook specifically for linked fringes operations
 */
export const useLinkedFringesActions = () => {
  const { loading, errors, linkedFringesActions } = useApiActions();

  return {
    loading: loading.fringes,
    error: errors.fringes,
    ...linkedFringesActions,
  };
};

export default useApiActions;
